import FlickityFade from 'flickity-fade';

import {isMobile} from '../util/media-query';

const selectors = {
  ariaLabel: 'aria-label',
  dataMediaId: 'data-media-id',
  dataTallLayout: 'data-tall-layout',
  dataThumb: 'data-thumb',
  dataThumbIndex: 'data-thumb-index',
  deferredMediaButton: '[data-deferred-media-button]',
  focusedElement: 'model-viewer, video, iframe, button, [href], input, [tabindex]',
  isActive: '.is-active',
  mediaType: 'data-type',
  mobileSliderDisabled: 'data-slideshow-disabled-mobile',
  productSlideshow: '[data-product-slideshow]',
  productThumbs: '[data-product-thumbs]',
  thumb: '[data-thumb-item]',
  thumbLink: '[data-thumb-link]',
  thumbSlider: '[data-thumbs-slider]',
  quickAddModal: '[data-quick-add-modal]',
  zoomElement: '[data-zoom-wrapper]',
};

const classes = {
  active: 'is-active',
  focused: 'is-focused',
  dragging: 'is-dragging',
  selected: 'is-selected',
  sliderEnabled: 'flickity-enabled',
  mediaHidden: 'media--hidden',
};

const attributes = {
  ariaCurrent: 'aria-current',
  sliderOptions: 'data-options',
};

let sections = {};

class InitSlider {
  constructor(section, modalHolder = null) {
    this.container = modalHolder || section.container;
    this.tallLayout = this.container.getAttribute(selectors.dataTallLayout) === 'true';
    this.slideshow = this.container.querySelector(selectors.productSlideshow);
    this.thumbSlider = this.container.querySelector(selectors.thumbSlider);
    this.thumbLinks = this.container.querySelectorAll(selectors.thumbLink);
    this.mobileSliderDisabled = this.container.getAttribute(selectors.mobileSliderDisabled) === 'true';
    this.initSliderMobileEvent = () => this.initSliderMobile();
    this.initSliderDesktopEvent = () => this.initSliderDesktop();

    if (this.slideshow && this.slideshow.hasAttribute(attributes.sliderOptions)) {
      this.customOptions = JSON.parse(decodeURIComponent(this.slideshow.getAttribute(attributes.sliderOptions)));
    }

    this.flkty = null;

    this.init();

    document.addEventListener('theme:product:galleryUpdate', ()=>{
      this.slideshow = this.container.querySelector(selectors.productSlideshow);
      this.thumbSlider = this.container.querySelector(selectors.thumbSlider);
      this.thumbLinks = this.container.querySelectorAll(selectors.thumbLink);
      this.mobileSliderDisabled = this.container.getAttribute(selectors.mobileSliderDisabled) === 'true';

      if (this.tallLayout) {
        if (!this.mobileSliderDisabled) {
          this.initSliderMobile();

        }
      } else if (this.mobileSliderDisabled) {
        this.initSliderDesktop();

      } else {
        this.createSlider();
      }
    });
  }

  init() {
    if (!this.slideshow) return;

    if (this.tallLayout) {
      if (!this.mobileSliderDisabled) {
        this.initSliderMobile();

        document.addEventListener('theme:resize:width', this.initSliderMobileEvent);
      }
    } else if (this.mobileSliderDisabled) {
      this.initSliderDesktop();

      document.addEventListener('theme:resize:width', this.initSliderDesktopEvent);
    } else {
      this.createSlider();
    }
  }

  initSliderMobile() {
    if (isMobile()) {
      this.createSlider();
    } else {
      this.destroySlider();
    }
  }

  initSliderDesktop() {
    if (isMobile()) {
      this.destroySlider();
    } else {
      this.createSlider();
    }
  }

  destroySlider() {
    const isSliderInitialized = this.slideshow.classList.contains(classes.sliderEnabled);

    if (isSliderInitialized) {
      this.flkty.destroy();
    }
  }

  createSlider() {
    if (!this.slideshow || (this.mobileSliderDisabled && isMobile())) {
      return;
    }

    const sliderOptions = {
      autoPlay: false,
      pageDots: false,
      wrapAround: true,
      ...this.customOptions,
    };

    const instance = this;
    const firstSlide = this.slideshow.querySelectorAll(`[${selectors.mediaType}]`)[0];
    let options = {
      ...sliderOptions,
      on: {
        ready: function () {
          const slides = this.element;
          slides.addEventListener('keyup', (e) => {
            if (e.code === 'Enter') {
              const zoomElement = slides.querySelector(`.${classes.selected} ${selectors.zoomElement}`);
              if (zoomElement) {
                zoomElement.dispatchEvent(new Event('click', {bubbles: false}));
                window.accessibility.lastElement = slides;
              }
            }
          });

          instance.sliderThumbs(this);

          instance.accessibilityActions(this);
        },
      },
    };

    this.flkty = new FlickityFade(this.slideshow, options);
    this.flkty.resize();

    if (firstSlide) {
      const firstType = firstSlide.getAttribute(selectors.mediaType);

      if (firstType === 'model' || firstType === 'video' || firstType === 'external_video') {
        this.flkty.options.draggable = false;
        this.flkty.updateDraggable();
      }
    }

    this.flkty.on('change', function (index) {
      let lastSLideIdx = index;

      if (instance.thumbSlider) {
        const selectedElem = instance.thumbSlider.querySelector(selectors.isActive);
        const currentSlide = instance.thumbSlider.querySelector(`${selectors.thumb} [${selectors.dataThumbIndex}="${index}"]`);

        if (selectedElem) {
          const selectedElemThumb = selectedElem.querySelector(`[${selectors.dataThumbIndex}]`);
          lastSLideIdx = Array.from(selectedElem.parentElement.children).indexOf(selectedElem);
          selectedElem.classList.remove(classes.active);

          if (selectedElemThumb) {
            selectedElemThumb.setAttribute(attributes.ariaCurrent, false);
          }
        }

        if (currentSlide) {
          currentSlide.parentElement.classList.add(classes.active);
          currentSlide.setAttribute(attributes.ariaCurrent, true);
        }

        instance.scrollToThumb();
      }

      const currentMedia = this.cells[lastSLideIdx].element;
      const newMedia = this.selectedElement;

      currentMedia.dispatchEvent(new CustomEvent('theme:media:hidden'));
      newMedia.classList.remove(classes.mediaHidden);
    });

    this.flkty.on('settle', function () {
      const currentMedia = this.selectedElement;
      const otherMedia = Array.prototype.filter.call(currentMedia.parentNode.children, function (child) {
        return child !== currentMedia;
      });
      const mediaType = currentMedia.getAttribute(selectors.mediaType);
      const isFocusEnabled = document.body.classList.contains(classes.focused);

      if (mediaType === 'model' || mediaType === 'video' || mediaType === 'external_video') {
        // fisrt boolean sets value, second option false to prevent refresh
        instance.flkty.options.draggable = false;
        instance.flkty.updateDraggable();
      } else {
        instance.flkty.options.draggable = true;
        instance.flkty.updateDraggable();
      }

      if (isFocusEnabled) currentMedia.dispatchEvent(new Event('focus'));

      if (otherMedia.length) {
        otherMedia.forEach((element) => {
          element.classList.add(classes.mediaHidden);
        });
      }

      currentMedia.dispatchEvent(new CustomEvent('theme:media:visible'));

      // Force media loading if slide becomes visible
      const deferredMedia = currentMedia.querySelector('deferred-media');
      if (deferredMedia && deferredMedia.getAttribute('loaded') !== true) {
        currentMedia.querySelector(selectors.deferredMediaButton).dispatchEvent(new Event('click', {bubbles: false}));
      }

      instance.accessibilityActions(this);
    });

    this.flkty.on('dragStart', (event, pointer) => {
      event.target.classList.add(classes.dragging);
    });

    this.flkty.on('dragEnd', (event, pointer) => {
      const draggedElem = this.flkty.element.querySelector(`.${classes.dragging}`);
      if (draggedElem) {
        draggedElem.classList.remove(classes.dragging);
      }
    });
  }

  accessibilityActions(slider) {
    const slides = slider.slides;

    if (slides.length) {
      slides.forEach((element) => {
        const slide = element.cells[0].element;
        const focusedElements = slide.querySelectorAll(selectors.focusedElement);

        if (slide.classList.contains(classes.selected)) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }

        if (focusedElements.length) {
          focusedElements.forEach((focusedElement) => {
            if (slide.classList.contains(classes.selected)) {
              focusedElement.removeAttribute('tabindex');
            } else {
              focusedElement.setAttribute('tabindex', '-1');
            }
          });
        }
      });
    }
  }

  scrollToThumb() {
    const thumbs = this.thumbSlider;

    if (thumbs) {
      const thumb = thumbs.querySelector(selectors.isActive);
      if (!thumb) return;
      const thumbsScrollTop = thumbs.scrollTop;
      const thumbsScrollLeft = thumbs.scrollLeft;
      const thumbsWidth = thumbs.offsetWidth;
      const thumbsHeight = thumbs.offsetHeight;
      const thumbsPositionBottom = thumbsScrollTop + thumbsHeight;
      const thumbsPositionRight = thumbsScrollLeft + thumbsWidth;
      const thumbPosTop = thumb.offsetTop;
      const thumbPosLeft = thumb.offsetLeft;
      const thumbWidth = thumb.offsetWidth;
      const thumbHeight = thumb.offsetHeight;
      const thumbRightPos = thumbPosLeft + thumbWidth;
      const thumbBottomPos = thumbPosTop + thumbHeight;
      const topCheck = thumbsScrollTop > thumbPosTop;
      const bottomCheck = thumbBottomPos > thumbsPositionBottom;
      const leftCheck = thumbsScrollLeft > thumbPosLeft;
      const rightCheck = thumbRightPos > thumbsPositionRight;
      const verticalCheck = bottomCheck || topCheck;
      const horizontalCheck = rightCheck || leftCheck;
      const isMobileView = isMobile();

      if (verticalCheck || horizontalCheck) {
        let scrollTopPosition = thumbPosTop - thumbsHeight + thumbHeight;
        let scrollLeftPosition = thumbPosLeft - thumbsWidth + thumbWidth;

        if (topCheck) {
          scrollTopPosition = thumbPosTop;
        }

        if (rightCheck && isMobileView) {
          scrollLeftPosition += parseInt(window.getComputedStyle(thumbs).paddingRight);
        }

        if (leftCheck) {
          scrollLeftPosition = thumbPosLeft;

          if (isMobileView) {
            scrollLeftPosition -= parseInt(window.getComputedStyle(thumbs).paddingLeft);
          }
        }

        thumbs.scrollTo({
          top: scrollTopPosition,
          left: scrollLeftPosition,
          behavior: 'smooth',
        });
      }
    }
  }

  sliderThumbs(slider) {
    if (this.thumbLinks.length) {
      this.thumbLinks.forEach((item) => {
        item.addEventListener('click', (e) => {
          e.preventDefault();
          const slideIdx = item.hasAttribute(selectors.dataThumbIndex) ? parseInt(item.getAttribute(selectors.dataThumbIndex)) : 0;

          this.flkty.select(slideIdx);
        });

        item.addEventListener('keyup', (e) => {
          // On keypress Enter move the focus to the first focusable element in the related slide
          if (e.code === 'Enter') {
            const mediaId = item.getAttribute(selectors.dataMediaId);
            const mediaElem = slider.element
              .querySelector(`[${selectors.dataMediaId}="${mediaId}"]`)
              .querySelectorAll('model-viewer, video, iframe, button, [href], input, [tabindex]:not([tabindex="-1"])')[0];
            if (mediaElem) {
              mediaElem.dispatchEvent(new Event('focus'));
              mediaElem.dispatchEvent(new Event('select'));
            }
          }
        });
      });
    }
  }

  onUnload() {
    if (this.tallLayout) {
      if (!this.mobileSliderDisabled) {
        document.removeEventListener('theme:resize:width', this.initSliderMobileEvent);
      }
    } else if (this.mobileSliderDisabled) {
      document.removeEventListener('theme:resize:width', this.initSliderDesktopEvent);
    }
  }
}

const initSlider = {
  onLoad() {
    sections[this.id] = new InitSlider(this);
  },
  onUnload(e) {
    sections[this.id].onUnload(e);
  },
};

export {initSlider, InitSlider};
